import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	Typography,
	TextField,
	Autocomplete,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadFileRequest } from 'redux/reducers/emProductFileUploads/reducer';
import { TEmProductFileUploadsRequest } from 'redux/reducers/emProductFileUploads/types';
import { selectUploadFileLoading } from 'redux/reducers/emProductFileUploads/selectors';
import { getEMerchant } from 'redux/reducers/monthlyFee/selectors';

type TUploadProductsDialogProps = {
	open: boolean;
	onClose: () => void;
	defaultEmId?: number | null;
	uploadedFilesRequest: TEmProductFileUploadsRequest;
};

export const UploadProductsDialog: FC<TUploadProductsDialogProps> = ({
	open,
	onClose,
	defaultEmId,
	uploadedFilesRequest,
}) => {
	const dispatch = useDispatch();
	const uploading = useSelector(selectUploadFileLoading);
	const emList = useSelector(getEMerchant);

	const [selectedEmId, setSelectedEmId] = useState<number | null>(defaultEmId ?? null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [emError, setEmError] = useState('');
	const [fileError, setFileError] = useState('');

	const hasPredefinedEm = useMemo(
		() => typeof defaultEmId === 'number' && defaultEmId > 0,
		[defaultEmId],
	);

	useEffect(() => {
		if (!open) return;
		setSelectedEmId(defaultEmId ?? null);
		setSelectedFile(null);
		setEmError('');
		setFileError('');
	}, [open, defaultEmId]);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] ?? null;
		if (file && file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
			setSelectedFile(null);
			setFileError('Only CSV files are accepted');
			return;
		}
		setSelectedFile(file);
		setFileError('');
		// reset value to allow same file re-select without mutating function param directly
		const inputEl = event.target as HTMLInputElement;
		inputEl.value = '';
	};

	const handleUpload = () => {
		let hasError = false;
		if (!selectedEmId) {
			setEmError('EM is required');
			hasError = true;
		}
		if (!selectedFile) {
			setFileError('File is required');
			hasError = true;
		}
		if (hasError) return;

		const form = new FormData();
		form.append('file', selectedFile as File);
		form.append('emId', String(selectedEmId));
		dispatch(uploadFileRequest({ file: form, uploadedFilesRequest }));
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle
				color="primary"
				sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
			>
				Upload products
			</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					{hasPredefinedEm ? (
						<Box>
							<Typography variant="body2" sx={{ color: 'text.secondary' }}>
								EM
							</Typography>
							<Typography variant="body1" fontWeight="bold">
								{emList?.find(item => item.id === selectedEmId)?.fullName ??
									`ID: ${String(selectedEmId)}`}
							</Typography>
						</Box>
					) : (
						<FormControl error={!!emError}>
							<Autocomplete
								fullWidth
								options={emList ?? []}
								getOptionLabel={option => option.fullName}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								value={emList?.find(item => item.id === selectedEmId) ?? null}
								onChange={(event, newValue) => {
									setSelectedEmId(newValue?.id ?? null);
									setEmError('');
								}}
								renderInput={params => (
									<TextField {...params} label="EM" error={!!emError} helperText={emError} />
								)}
							/>
						</FormControl>
					)}

					<Box>
						<Button
							component="label"
							variant="outlined"
							sx={{ textTransform: 'none' }}
							color={fileError ? 'error' : 'primary'}
							disabled={uploading}
						>
							{selectedFile ? 'Change file' : 'Select CSV file'}
							<input type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
						</Button>
						{selectedFile && (
							<Typography variant="body2" sx={{ mt: 0.5 }}>
								Selected: {selectedFile.name}
							</Typography>
						)}
						{!!fileError && (
							<FormHelperText error sx={{ ml: 1 }}>
								{fileError}
							</FormHelperText>
						)}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={uploading}>
					Cancel
				</Button>
				<Button
					onClick={handleUpload}
					color="primary"
					disabled={uploading || !selectedEmId || !selectedFile}
				>
					Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};
